import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Edit2, Plus, Search, Trash2 } from 'lucide-react'
import { PaginatedTabTable } from '@/components/shared/PaginatedTabTable'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { AssetFormDialog } from '@/components/clients/tabs/AssetFormDialog'
import { DateRangeFilter } from '@/components/clients/tabs/DateRangeFilter'
import {
  ClientTabSection,
  clientTabFilterOptionClass,
  clientTabFilterTriggerClass,
  clientTabPrimaryBtnClass,
  clientTabSearchInputClass,
  clientTabFirstTdClass,
} from '@/components/clients/tabs/ClientTabSection'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import {
  useClientAssets,
  useCreateClientAsset,
  useDeleteClientAsset,
  useUpdateClientAsset,
} from '@/hooks/useClients'
import { useListParams } from '@/hooks/useListParams'
import { formatDate } from '@/lib/format'
import { getApiErrorMessage } from '@/services/api'
import {
  type ClientAsset,
  type ClientAssetListQuery,
  type ClientAssetOrigin,
  type ClientAssetRequest,
} from '@/types'

const ALL = 'all'
const figmaIconButtonClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-[5px] text-[#171a20] transition-colors disabled:pointer-events-none disabled:opacity-50'

type AssetFilters = Pick<
  ClientAssetListQuery,
  'origin' | 'date_from' | 'date_to'
>

function displayDash(value: string | null | undefined) {
  return value && value.trim() ? value : '--'
}

function OriginBadge({
  asset,
  onReportClick,
}: {
  asset: ClientAsset
  onReportClick?: () => void
}) {
  const { t } = useTranslation('clients')
  const isReport = asset.origin === 'report'
  const badge = (
    <Badge
      className={
        isReport
          ? 'h-6 rounded-lg border-0 bg-[#dbeafe] px-1.5 py-1 text-xs font-medium leading-4 text-[#1d44b3] hover:bg-[#dbeafe]'
          : 'h-6 rounded-lg border-0 bg-[#f4f5f8] px-1.5 py-1 text-xs font-medium leading-4 text-[#171a20] hover:bg-[#f4f5f8]'
      }
    >
      {isReport ? t('assets.source.report') : t('assets.source.manual')}
    </Badge>
  )

  if (isReport && onReportClick) {
    return (
      <button
        type='button'
        onClick={e => {
          e.stopPropagation()
          onReportClick()
        }}
      >
        {badge}
      </button>
    )
  }

  return badge
}

// 2.4.11 Client details — assets/equipment tab (CR 86eymqeup).
export function AssetsTab({ clientId }: { clientId: string }) {
  const { t } = useTranslation('clients')
  const navigate = useNavigate()
  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useListParams<AssetFilters>({})
  const { data, isLoading, isError } = useClientAssets(clientId, query)
  const createAsset = useCreateClientAsset(clientId)
  const updateAsset = useUpdateClientAsset(clientId)
  const deleteAsset = useDeleteClientAsset(clientId)

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<ClientAsset | null>(null)

  const isSaving = createAsset.isPending || updateAsset.isPending

  const originFilter = filters.origin ?? ALL

  const openCreate = () => {
    setEditing(null)
    setOpen(true)
  }

  const openEdit = (asset: ClientAsset) => {
    if (asset.origin !== 'manual') return
    setEditing(asset)
    setOpen(true)
  }

  const closePanel = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setEditing(null)
    }
  }

  const submitAsset = async (body: ClientAssetRequest) => {
    try {
      if (editing) {
        await updateAsset.mutateAsync({
          assetId: editing.id,
          body,
        })
      } else {
        await createAsset.mutateAsync(body)
      }
      toast({
        variant: 'success',
        title: editing ? t('assets.toast.updated') : t('assets.toast.created'),
      })
      closePanel(false)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('assets.toast.error')),
      })
    }
  }

  const remove = async (asset: ClientAsset) => {
    if (asset.origin !== 'manual') return
    try {
      await deleteAsset.mutateAsync(asset.id)
      toast({ variant: 'success', title: t('assets.toast.deleted') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('assets.toast.deleteError')),
      })
    }
  }

  const toolbar = useMemo(
    () => (
      <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
          <DateRangeFilter
            appearance='field'
            triggerClassName={FORM_CONTROL_FOCUS_CLASS}
            label={t('assets.filters.installationDate')}
            from={filters.date_from}
            to={filters.date_to}
            onChange={setFilters}
          />

          <Select
            value={originFilter}
            onValueChange={value =>
              setFilters({
                origin:
                  value === ALL ? undefined : (value as ClientAssetOrigin),
              })
            }
          >
            <SelectTrigger className={clientTabFilterTriggerClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL} className={clientTabFilterOptionClass}>
                {t('assets.filters.allTypes')}
              </SelectItem>
              <SelectItem value='manual' className={clientTabFilterOptionClass}>
                {t('assets.filters.manual')}
              </SelectItem>
              <SelectItem value='report' className={clientTabFilterOptionClass}>
                {t('assets.filters.report')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end'>
          <div className='relative w-full sm:w-[240px]'>
            <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#171a20]' />
            <Input
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              placeholder={t('assets.searchPlaceholder')}
              className={`${clientTabSearchInputClass} h-10 rounded-xl pl-10`}
            />
          </div>

          <Button className={clientTabPrimaryBtnClass} onClick={openCreate}>
            <Plus className='size-4' />
            {t('assets.actions.add')}
          </Button>
        </div>
      </div>
    ),
    [
      filters.date_from,
      filters.date_to,
      onSearchChange,
      originFilter,
      search,
      setFilters,
      t,
    ]
  )

  return (
    <ClientTabSection title={t('assets.title')} toolbar={toolbar}>
      <PaginatedTabTable<ClientAsset>
        clientHistoryStyle
        data={data}
        isLoading={isLoading}
        isError={isError}
        emptyMessage={t('assets.empty')}
        errorMessage={t('assets.error')}
        page={page}
        pageSize={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        onRowClick={openEdit}
        isRowClickable={a => a.origin === 'manual'}
        rowKey={a => a.id}
        columns={[
          {
            header: t('assets.columns.name'),
            cellClassName: clientTabFirstTdClass,
            cell: a => (
              <div className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                {a.label}
              </div>
            ),
          },
          {
            header: t('assets.columns.serial'),
            cell: a => (
              <span className='text-sm font-normal text-[#171a20]'>
                {displayDash(
                  a.serial ?? (a.meta?.serial as string | undefined)
                )}
              </span>
            ),
          },
          {
            header: t('assets.columns.location'),
            cell: a => (
              <span className='text-sm font-normal text-[#171a20]'>
                {displayDash(a.location)}
              </span>
            ),
          },
          {
            header: t('assets.columns.installationDate'),
            className: 'w-[200px]',
            cell: a => (
              <span className='text-sm font-normal text-[#171a20]'>
                {a.installation_date ? formatDate(a.installation_date) : '--'}
              </span>
            ),
          },
          {
            header: t('assets.columns.source'),
            className: 'w-[140px] text-center',
            cell: a => {
              const jobId = a.job_id ?? (a.meta?.job_id as string | undefined)
              return (
                <OriginBadge
                  asset={a}
                  onReportClick={
                    a.source_report_url
                      ? () => navigate(a.source_report_url as string)
                      : jobId
                        ? () => navigate(`/calendar?job=${jobId}&tab=reports`)
                        : undefined
                  }
                />
              )
            },
          },
          {
            header: t('list.columns.actions'),
            className: 'w-[96px] text-right',
            cell: a =>
              a.origin === 'manual' ? (
                <div className='flex items-center justify-end gap-2'>
                  <TruncatedTextTooltip
                    alwaysShow
                    closeOnPointerDown
                    className='inline-flex shrink-0'
                    content={t('assets.actions.delete')}
                    delayDuration={0}
                  >
                    <button
                      type='button'
                      className={`${figmaIconButtonClass} hover:border-[#d63b33] hover:bg-[#fef2f2] hover:text-[#d63b33]`}
                      aria-label={t('assets.actions.delete')}
                      disabled={deleteAsset.isPending}
                      onClick={e => {
                        e.stopPropagation()
                        void remove(a)
                      }}
                    >
                      <Trash2 className='size-4' />
                    </button>
                  </TruncatedTextTooltip>
                  <TruncatedTextTooltip
                    alwaysShow
                    closeOnPointerDown
                    className='inline-flex shrink-0'
                    content={t('assets.actions.edit')}
                    delayDuration={0}
                  >
                    <button
                      type='button'
                      className={`${figmaIconButtonClass} hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]`}
                      aria-label={t('assets.actions.edit')}
                      onClick={e => {
                        e.stopPropagation()
                        openEdit(a)
                      }}
                    >
                      <Edit2 className='size-4' />
                    </button>
                  </TruncatedTextTooltip>
                </div>
              ) : (
                <span aria-hidden='true' />
              ),
          },
        ]}
      />

      <AssetFormDialog
        open={open}
        asset={editing}
        isSaving={isSaving}
        onOpenChange={closePanel}
        onSubmit={submitAsset}
      />
    </ClientTabSection>
  )
}
